import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Row,
  Col,
  Spin,
  Button,
  Space,
  Tag,
  Modal,
  Alert,
  Progress,
  Tabs,
  Select,
  Checkbox,
  Flex,
  Statistic,
  message,
} from 'antd';
import { ReloadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { PageIntro, PageLayout, PagePanel } from '../../components';
import whatsappMonitoringService, {
  QueueHealth,
  FailedJob,
  DelayedJob,
  ActiveJob,
} from '../../services/whatsapp-monitoring.service';

export const WhatsappMonitoring: React.FC = () => {
  const [health, setHealth] = useState<QueueHealth | null>(null);
  const [failedJobs, setFailedJobs] = useState<FailedJob[]>([]);
  const [delayedJobs, setDelayedJobs] = useState<DelayedJob[]>([]);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5000);
  const [selectedJob, setSelectedJob] = useState<FailedJob | null>(null);
  const [showJobDetail, setShowJobDetail] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [healthRes, failedRes, delayedRes, activeRes] = await Promise.all([
        whatsappMonitoringService.getQueueHealth(),
        whatsappMonitoringService.getFailedJobs(50),
        whatsappMonitoringService.getDelayedJobs(50),
        whatsappMonitoringService.getActiveJobs(50),
      ]);

      setHealth(healthRes.data);
      setFailedJobs(failedRes.data);
      setDelayedJobs(delayedRes.data);
      setActiveJobs(activeRes.data);
    } catch (err) {
      message.error(
        err instanceof Error ? err.message : 'Erro ao carregar dados',
      );
      console.error('Erro ao carregar dados de monitoramento:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    if (!autoRefresh) return;

    const interval = setInterval(loadData, refreshInterval);
    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  const handleClearFailedJobs = async () => {
    Modal.confirm({
      title: 'Remover Jobs Falhados',
      content: `Tem certeza que quer remover ${failedJobs.length} jobs falhados?`,
      okText: 'Remover',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await whatsappMonitoringService.clearFailedJobs();
          message.success(result.message);
          await loadData();
        } catch (err) {
          message.error(
            err instanceof Error ? err.message : 'Erro ao limpar jobs',
          );
        }
      },
    });
  };

  const handleCleanOldJobs = async () => {
    Modal.confirm({
      title: 'Limpar Jobs Antigos',
      content:
        'Tem certeza que quer remover jobs completados com mais de 24 horas?',
      okText: 'Limpar',
      cancelText: 'Cancelar',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await whatsappMonitoringService.cleanOldJobs(24);
          message.success(result.message);
          await loadData();
        } catch (err) {
          message.error(
            err instanceof Error ? err.message : 'Erro ao limpar jobs',
          );
        }
      },
    });
  };

  const failedColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '15%',
      render: (text: string) => (
        <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
          {String(text).substring(0, 12)}...
        </span>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Tag>{text}</Tag>,
    },
    {
      title: 'Destinatário',
      dataIndex: ['data', 'to'],
      key: 'to',
      render: (text: string) => text || '-',
    },
    {
      title: 'Motivo',
      dataIndex: 'failedReason',
      key: 'failedReason',
      render: (text: string) =>
        text ? (
          <span style={{ fontSize: '0.85rem' }}>
            {text.substring(0, 40)}...
          </span>
        ) : (
          '-'
        ),
    },
    {
      title: 'Tent.',
      dataIndex: 'attemptsMade',
      key: 'attemptsMade',
      width: '8%',
    },
    {
      title: 'Ação',
      key: 'action',
      width: '10%',
      render: (_: any, record: FailedJob) => (
        <Button
          type="primary"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedJob(record);
            setShowJobDetail(true);
          }}
        >
          Detalhe
        </Button>
      ),
    },
  ];

  const delayedColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '15%',
      render: (text: string) => (
        <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
          {String(text).substring(0, 12)}...
        </span>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'Destinatário',
      dataIndex: ['data', 'to'],
      key: 'to',
    },
    {
      title: 'Próxima Tentativa',
      dataIndex: 'delayUntil',
      key: 'delayUntil',
      render: (text: string) =>
        new Date(text).toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      title: 'Tent.',
      dataIndex: 'attemptsMade',
      key: 'attemptsMade',
      width: '8%',
    },
  ];

  const activeColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '15%',
      render: (text: string) => (
        <span style={{ fontSize: '0.85rem', fontFamily: 'monospace' }}>
          {String(text).substring(0, 12)}...
        </span>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <Tag color="processing">{text}</Tag>,
    },
    {
      title: 'Destinatário',
      dataIndex: ['data', 'to'],
      key: 'to',
    },
    {
      title: 'Tentativa',
      dataIndex: 'attemptsMade',
      key: 'attemptsMade',
      render: (text: number) => text + 1,
      width: '8%',
    },
    {
      title: 'Progresso',
      dataIndex: 'progress',
      key: 'progress',
      width: '20%',
      render: (progress: number) => (
        <Progress percent={progress || 0} size="small" />
      ),
    },
  ];

  const renderHealthStatus = () => {
    if (!health) return null;

    return (
      <div>
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Processando"
                value={health.stats.active}
                suffix="jobs"
                prefix="⚙️"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Aguardando"
                value={health.stats.waiting}
                suffix="jobs"
                prefix="⏳"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Atrasados"
                value={health.stats.delayed}
                suffix="jobs"
                prefix="⏱️"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Falhados"
                value={health.stats.failed}
                suffix="jobs"
                prefix="❌"
                valueStyle={health.stats.failed > 0 ? { color: '#cf1322' } : {}}
              />
            </Card>
          </Col>
        </Row>

        {health.alerts.length > 0 && (
          <Alert
            message="⚠️ Alertas Detectados"
            description={
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                {health.alerts.map((alert, idx) => (
                  <li key={idx}>{alert}</li>
                ))}
              </ul>
            }
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {health.stats.failed === 0 &&
          health.alerts.length === 0 &&
          health.stats.delayed === 0 && (
            <Alert
              message="✓ Fila em perfeito funcionamento"
              type="success"
              showIcon
            />
          )}

        <div style={{ fontSize: '0.85rem', color: '#999', marginTop: 16 }}>
          Atualizado em:{' '}
          {new Date(health.timestamp).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          })}
        </div>
      </div>
    );
  };

  const tabItems = [
    {
      key: 'health',
      label: (
        <>
          <span>💚 Saúde da Fila</span>
          {health && !health.healthy && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              Alertas
            </Tag>
          )}
        </>
      ),
      children: renderHealthStatus(),
    },
    {
      key: 'failed',
      label: (
        <>
          <span>❌ Falhados</span>
          {failedJobs.length > 0 && (
            <Tag color="red" style={{ marginLeft: 8 }}>
              {failedJobs.length}
            </Tag>
          )}
        </>
      ),
      children: (
        <div>
          {failedJobs.length > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleClearFailedJobs}
              style={{ marginBottom: 16 }}
            >
              Limpar {failedJobs.length} Falhados
            </Button>
          )}
          {failedJobs.length === 0 ? (
            <Alert
              message="✓ Nenhum job com falha permanente"
              type="success"
              showIcon
            />
          ) : (
            <Table
              columns={failedColumns}
              dataSource={failedJobs}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          )}
        </div>
      ),
    },
    {
      key: 'delayed',
      label: (
        <>
          <span>⏱️ Atrasados</span>
          {delayedJobs.length > 0 && (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              {delayedJobs.length}
            </Tag>
          )}
        </>
      ),
      children: (
        <div>
          {delayedJobs.length === 0 ? (
            <Alert message="✓ Nenhum job atrasado" type="success" showIcon />
          ) : (
            <Table
              columns={delayedColumns}
              dataSource={delayedJobs}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          )}
        </div>
      ),
    },
    {
      key: 'active',
      label: (
        <>
          <span>⚙️ Ativos</span>
          {activeJobs.length > 0 && (
            <Tag color="blue" style={{ marginLeft: 8 }}>
              {activeJobs.length}
            </Tag>
          )}
        </>
      ),
      children: (
        <div>
          {activeJobs.length === 0 ? (
            <Alert
              message="ℹ️ Nenhum job sendo processado"
              type="info"
              showIcon
            />
          ) : (
            <Table
              columns={activeColumns}
              dataSource={activeJobs}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              size="small"
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <PageLayout>
      <PageIntro
        badge="Monitoramento"
        icon="📊"
        title="Fila de Mensagens WhatsApp"
        description="Acompanhe em tempo real o status de envio de mensagens, jobs falhados, atrasados e em processamento"
      />
      <PagePanel>
        <Spin spinning={loading && !health}>
          <Card style={{ marginBottom: 24 }}>
            <Flex
              justify="space-between"
              align="center"
              style={{ marginBottom: 16 }}
            >
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadData}
                  loading={loading}
                >
                  Atualizar Agora
                </Button>
              </Space>
              <Space>
                <Checkbox
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                >
                  Auto-atualizar
                </Checkbox>
                {autoRefresh && (
                  <Select
                    value={refreshInterval}
                    onChange={(value) => setRefreshInterval(value)}
                    style={{ width: 100 }}
                    options={[
                      { label: '3 segundos', value: 3000 },
                      { label: '5 segundos', value: 5000 },
                      { label: '10 segundos', value: 10000 },
                      { label: '30 segundos', value: 30000 },
                    ]}
                  />
                )}
              </Space>
            </Flex>

            <Tabs items={tabItems} />
          </Card>

          <Card title="⚙️ Ações de Limpeza">
            <Space>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleClearFailedJobs}
                disabled={failedJobs.length === 0}
              >
                Remover Jobs Falhados ({failedJobs.length})
              </Button>
              <Button onClick={handleCleanOldJobs} icon={<DeleteOutlined />}>
                Limpar Jobs Antigos (&gt;24h)
              </Button>
            </Space>
          </Card>
        </Spin>
      </PagePanel>

      {/* Modal de Detalhes do Job */}
      <Modal
        title="Detalhes do Job Falhado"
        open={showJobDetail}
        onCancel={() => setShowJobDetail(false)}
        footer={[
          <Button key="close" onClick={() => setShowJobDetail(false)}>
            Fechar
          </Button>,
        ]}
        width={800}
      >
        {selectedJob && (
          <div>
            <Card size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <div>
                    <strong>ID do Job:</strong>
                    <div
                      style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                    >
                      {selectedJob.id}
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <strong>Tipo:</strong>
                    <div>
                      <Tag>{selectedJob.name}</Tag>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <strong>Destinatário:</strong>
                    <div>{selectedJob.data?.to || '-'}</div>
                  </div>
                </Col>
                <Col span={12}>
                  <div>
                    <strong>Tentativas:</strong>
                    <div>{selectedJob.attemptsMade}</div>
                  </div>
                </Col>
              </Row>
            </Card>

            <Card
              size="small"
              title="Motivo da Falha"
              style={{ marginBottom: 16 }}
            >
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 150,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {selectedJob.failedReason || 'Sem informações'}
              </div>
            </Card>

            {selectedJob.stacktrace && selectedJob.stacktrace.length > 0 && (
              <Card
                size="small"
                title="Stack Trace"
                style={{ marginBottom: 16 }}
              >
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '0.85rem',
                    backgroundColor: '#1f1f1f',
                    color: '#fff',
                    padding: 12,
                    borderRadius: 4,
                    maxHeight: 200,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {selectedJob.stacktrace.join('\n')}
                </div>
              </Card>
            )}

            <Card size="small" title="Dados do Job">
              <div
                style={{
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  backgroundColor: '#f5f5f5',
                  padding: 12,
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(selectedJob.data, null, 2)}
              </div>
            </Card>
          </div>
        )}
      </Modal>
    </PageLayout>
  );
};

export default WhatsappMonitoring;
